import React from 'react';

/**
 * Reusable Loading Spinner component
 * 
 * @param {Object} props
 * @param {string} props.size - 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} props.color - Tailwind color class (e.g., 'primary-600', 'white')
 * @param {string} props.className - Additional classes
 * @param {boolean} props.fullPage - If true, centers the spinner in the viewport
 * @param {string} props.label - Optional loading text
 */
export default function LoadingSpinner({ 
  size = 'md', 
  color = 'primary-600',
  className = '',
  fullPage = false,
  label
}) {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
    lg: 'w-8 h-8 border-3',
    xl: 'w-12 h-12 border-4',
  };

  const spinner = (
    <div className={`${fullPage ? 'flex flex-col items-center justify-center gap-3' : 'inline-flex items-center gap-2'}`}>
      <div
        className={`
          ${sizeClasses[size]}
          border-${color}
          border-t-transparent
          rounded-full
          animate-spin
          ${className}
        `}
        style={{ borderTopColor: 'transparent' }}
      />
      {label && (
        <span className={`text-${color} text-sm font-medium`}>
          {label}
        </span>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        {spinner}
      </div>
    );
  }

  return spinner;
}

/**
 * Section-level loader with skeleton UI
 */
export function SectionLoader({ 
  rows = 3, 
  className = ''
}) {
  return (
    <div className={`space-y-4 animate-pulse ${className}`}>
      {[...Array(rows)].map((_, i) => (
        <div 
          key={i} 
          className="h-16 bg-gray-200 dark:bg-gray-800 rounded-2xl"
        />
      ))}
    </div>
  );
}

/**
 * Card skeleton loader
 */
export function CardSkeleton({ 
  count = 4,
  className = ''
}) {
  return (
    <div className={`grid grid-cols-2 gap-3 ${className}`}>
      {[...Array(count)].map((_, i) => (
        <div 
          key={i} 
          className="h-24 bg-gray-200 dark:bg-gray-800 rounded-2xl animate-pulse"
        />
      ))}
    </div>
  );
}

/**
 * Full page skeleton with multiple sections
 */
export function PageSkeleton() {
  return (
    <div className="space-y-5 animate-pulse">
      {/* Header skeleton */}
      <div className="h-20 bg-gray-200 dark:bg-gray-800 rounded-2xl" />
      
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-200 dark:bg-gray-800 rounded-2xl" />
        ))}
      </div>
      
      {/* Content sections */}
      <div className="h-56 bg-gray-200 dark:bg-gray-800 rounded-2xl" />
      <div className="h-56 bg-gray-200 dark:bg-gray-800 rounded-2xl" />
    </div>
  );
}
